import { useEffect, useState, useCallback } from "react";
import izenuAPI from "../../services/izenuAPI";
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement,
    Title, Tooltip, Legend, Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

function fmtNum(n) {
    if (!n) return "0";
    const num = Number(n);
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
    return num.toLocaleString("pt-PT");
}

function fmtDuration(sec) {
    if (!sec) return "0s";
    const v = Math.round(Number(sec));
    if (v < 60) return v + "s";
    const m = Math.floor(v / 60);
    const r = v % 60;
    if (m < 60) return `${m}m ${r}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
}

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const COLORS = ['#6366f1', '#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function RadioStats() {
    const [radios, setRadios] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ radio_id: "", from: daysAgo(7), to: today() });

    const fetchRadios = useCallback(async () => {
        const { data } = await izenuAPI.get("/admin/radios/list.php");
        setRadios(data?.data || []);
    }, []);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.radio_id) params.set("radio_id", filters.radio_id);
            if (filters.from) params.set("from", filters.from);
            if (filters.to) params.set("to", filters.to);
            const { data } = await izenuAPI.get(`/admin/radio/stats.php?${params.toString()}`);
            setStats(data?.stats || null);
        } finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { fetchRadios(); }, [fetchRadios]);
    useEffect(() => { fetchStats(); }, [fetchStats]);

    const t = stats?.totals || {};
    const byDay = stats?.by_day || [];
    const byHour = stats?.by_hour || [];
    const byCountry = stats?.by_country || [];
    const byPlatform = stats?.by_platform || [];
    const retention = stats?.retention || {};
    const byRadio = stats?.by_radio || [];

    const dayChart = {
        labels: byDay.map((d) => d.day),
        datasets: [
            { label: "Tune-ins", data: byDay.map((d) => d.tune_ins), borderColor: "#6366f1", backgroundColor: "rgba(99,102,241,0.1)", fill: true, tension: 0.3 },
            { label: "Minutos", data: byDay.map((d) => d.minutes), borderColor: "#f59e0b", backgroundColor: "rgba(245,158,11,0.1)", fill: true, tension: 0.3, yAxisID: "y1" },
        ],
    };
    const hourBuckets = Array.from({ length: 24 }, (_, h) => {
        const row = byHour.find((r) => parseInt(r.hour) === h);
        return row ? parseInt(row.tune_ins) : 0;
    });
    const hourChart = { labels: Array.from({ length: 24 }, (_, h) => `${h}h`), datasets: [{ label: "Tune-ins", data: hourBuckets, backgroundColor: "#6366f1", borderRadius: 3 }] };
    const platformChart = { labels: byPlatform.map((p) => p.platform || "unknown"), datasets: [{ data: byPlatform.map((p) => p.tune_ins), backgroundColor: COLORS, borderWidth: 0 }] };
    const radioChart = { labels: byRadio.map((r) => r.radio_name || `#${r.radio_id}`), datasets: [{ data: byRadio.map((r) => r.tune_ins), backgroundColor: COLORS, borderWidth: 0 }] };
    const retentionChart = {
        labels: ["≥30s", "≥1min", "≥5min", "≥30min", "≥1h"],
        datasets: [{ label: "% sessões", data: [retention.pct_30s, retention.pct_1m, retention.pct_5m, retention.pct_30m, retention.pct_1h], backgroundColor: ["#10b981", "#3b82f6", "#f59e0b", "#f43f5e", "#8b5cf6"], borderRadius: 3 }],
    };

    const lineOpts = {
        maintainAspectRatio: false, responsive: true,
        scales: {
            y: { position: "left", grid: { color: "#f3f4f6" }, ticks: { color: "#888", font: { size: 11 } } },
            y1: { position: "right", grid: { display: false }, ticks: { color: "#888", font: { size: 11 } } },
            x: { grid: { display: false }, ticks: { color: "#888", font: { size: 11 } } },
        },
        plugins: { legend: { labels: { color: "#555", font: { size: 12 } } } },
    };
    const barOpts = {
        maintainAspectRatio: false, responsive: true,
        scales: { y: { grid: { color: "#f3f4f6" }, ticks: { color: "#888", font: { size: 11 } } }, x: { grid: { display: false }, ticks: { color: "#888", font: { size: 11 } } } },
        plugins: { legend: { display: false } },
    };
    const retentionOpts = {
        maintainAspectRatio: false, responsive: true,
        scales: { y: { min: 0, max: 100, grid: { color: "#f3f4f6" }, ticks: { color: "#888", font: { size: 11 }, callback: (v) => v + "%" } }, x: { grid: { display: false }, ticks: { color: "#888", font: { size: 11 } } } },
        plugins: { legend: { display: false } },
    };
    const doughnutOpts = {
        responsive: true, maintainAspectRatio: false, cutout: "65%",
        plugins: { legend: { position: "right", labels: { boxWidth: 12, padding: 10, font: { size: 11 }, color: "#555" } } },
    };

    const exportCSV = () => {
        const lines = [
            ["Rádio", "Tune-ins", "Minutos", "Dur. média (s)", "Ouvintes únicos"],
            ...byRadio.map((r) => [r.radio_name, r.tune_ins, r.minutes, r.avg_duration_sec, r.unique_listeners]),
        ];
        const csv = "﻿" + lines.map((l) => l.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `radio-stats-${filters.from}-${filters.to}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div style={s.container}>
            <div style={s.headerRow}>
                <div>
                    <h1 style={s.title}>Audiência das Rádios</h1>
                    <p style={s.subtitle}>Sessões, tempo ouvido, países, plataformas e retenção.</p>
                </div>
                <button onClick={exportCSV} style={s.primaryBtn}>Exportar CSV</button>
            </div>

            {/* Filtros */}
            <div style={s.filtersCard}>
                <Field label="Rádio">
                    <select value={filters.radio_id} onChange={(e) => setFilters({ ...filters, radio_id: e.target.value })} style={s.input}>
                        <option value="">Todas</option>
                        {radios.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                    </select>
                </Field>
                <Field label="De">
                    <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} style={s.input} />
                </Field>
                <Field label="Até">
                    <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} style={s.input} />
                </Field>
                <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
                    <button onClick={() => setFilters({ ...filters, from: daysAgo(1), to: today() })} style={s.chipBtn}>24h</button>
                    <button onClick={() => setFilters({ ...filters, from: daysAgo(7), to: today() })} style={s.chipBtn}>7d</button>
                    <button onClick={() => setFilters({ ...filters, from: daysAgo(30), to: today() })} style={s.chipBtn}>30d</button>
                </div>
            </div>

            {loading ? (
                <p style={s.empty}>A carregar...</p>
            ) : !stats ? (
                <p style={s.empty}>Sem dados.</p>
            ) : (
                <>
                    {/* Stat cards */}
                    <div style={s.row}>
                        <Stat label="Tune-ins" value={fmtNum(t.tune_ins)} color="#6366f1" icon="▶" />
                        <Stat label="Minutos ouvidos" value={fmtNum(t.total_minutes)} color="#f59e0b" icon="⏱" />
                        <Stat label="Duração média" value={fmtDuration(t.avg_duration_sec)} color="#10b981" icon="📊" />
                        <Stat label="Ouvintes únicos" value={fmtNum(t.unique_listeners)} color="#ec4899" icon="👥" />
                        <Stat label="Pico (5min)" value={fmtNum(stats.peak_concurrent_5min)} color="#8b5cf6" icon="🔥" />
                    </div>

                    {/* Tune-ins por dia */}
                    <div style={s.row}>
                        <div style={s.card}>
                            <h3 style={s.cardTitle}>Tune-ins por dia</h3>
                            <p style={s.cardDesc}>Número de sessões iniciadas e tempo ouvido (minutos) ao longo do período.</p>
                            <div style={{ height: 280 }}>
                                {byDay.length > 0 ? <Line data={dayChart} options={lineOpts} /> : <p style={s.empty}>Sem dados</p>}
                            </div>
                        </div>
                    </div>

                    {/* Hora + Por rádio */}
                    <div style={s.row}>
                        <div style={{ ...s.card, flex: "2 1 500px" }}>
                            <h3 style={s.cardTitle}>Distribuição por hora do dia</h3>
                            <p style={s.cardDesc}>Horas em que os ouvintes mais se ligam às rádios.</p>
                            <div style={{ height: 240 }}><Bar data={hourChart} options={barOpts} /></div>
                        </div>
                        <div style={s.card}>
                            <h3 style={s.cardTitle}>Por Rádio</h3>
                            <p style={s.cardDesc}>Peso relativo de cada rádio no total.</p>
                            <div style={{ height: 240 }}>
                                {byRadio.length > 0 ? <Doughnut data={radioChart} options={doughnutOpts} /> : <p style={s.empty}>Sem dados</p>}
                            </div>
                        </div>
                    </div>

                    {/* Plataformas + Retenção */}
                    <div style={s.row}>
                        <div style={s.card}>
                            <h3 style={s.cardTitle}>Plataformas</h3>
                            <p style={s.cardDesc}>Distribuição entre iOS, Android e web.</p>
                            <div style={{ height: 240 }}>
                                {byPlatform.length > 0 ? <Doughnut data={platformChart} options={doughnutOpts} /> : <p style={s.empty}>Sem dados</p>}
                            </div>
                        </div>
                        <div style={{ ...s.card, flex: "2 1 500px" }}>
                            <h3 style={s.cardTitle}>Retenção</h3>
                            <p style={s.cardDesc}>Percentagem de sessões que ultrapassaram cada marco de duração.</p>
                            <div style={{ height: 240 }}><Bar data={retentionChart} options={retentionOpts} /></div>
                        </div>
                    </div>

                    {/* Tabelas */}
                    <div style={s.row}>
                        <div style={s.card}>
                            <h3 style={s.cardTitle}>Top Países</h3>
                            <p style={s.cardDesc}>Onde os ouvintes estão localizados no momento da sessão.</p>
                            <table style={s.table}>
                                <thead>
                                    <tr><th style={s.th}>País</th><th style={{ ...s.th, textAlign: "right" }}>Tune-ins</th><th style={{ ...s.th, textAlign: "right" }}>Minutos</th></tr>
                                </thead>
                                <tbody>
                                    {byCountry.length === 0 ? (
                                        <tr><td colSpan="3" style={s.emptyCell}>Sem dados</td></tr>
                                    ) : byCountry.map((c, i) => (
                                        <tr key={i} style={s.tr}>
                                            <td style={s.td}>{c.country}</td>
                                            <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>{fmtNum(c.tune_ins)}</td>
                                            <td style={{ ...s.td, textAlign: "right", color: "#6b7280" }}>{fmtNum(c.minutes)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={s.card}>
                            <h3 style={s.cardTitle}>Por Rádio</h3>
                            <p style={s.cardDesc}>Métricas detalhadas por estação.</p>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Rádio</th>
                                        <th style={{ ...s.th, textAlign: "right" }}>Tune-ins</th>
                                        <th style={{ ...s.th, textAlign: "right" }}>Minutos</th>
                                        <th style={{ ...s.th, textAlign: "right" }}>Únicos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byRadio.length === 0 ? (
                                        <tr><td colSpan="4" style={s.emptyCell}>Sem dados</td></tr>
                                    ) : byRadio.map((r) => (
                                        <tr key={r.radio_id} style={s.tr}>
                                            <td style={s.td}>{r.radio_name}</td>
                                            <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>{fmtNum(r.tune_ins)}</td>
                                            <td style={{ ...s.td, textAlign: "right", color: "#6b7280" }}>{fmtNum(r.minutes)}</td>
                                            <td style={{ ...s.td, textAlign: "right", color: "#6b7280" }}>{fmtNum(r.unique_listeners)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ label, value, color, icon }) {
    return (
        <div style={s.statCard}>
            <div style={{ ...s.statIcon, backgroundColor: color + "15", color }}>{icon}</div>
            <div>
                <p style={s.statLabel}>{label}</p>
                <h2 style={{ ...s.statValue, color }}>{value}</h2>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label style={{ display: "block", fontSize: 11, color: "#888", fontWeight: 500, marginBottom: 4 }}>{label}</label>
            {children}
        </div>
    );
}

const s = {
    container: { padding: "0 20px 40px 20px", maxWidth: 1300 },
    headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700, color: "#1a1a2e", margin: 0 },
    subtitle: { margin: "4px 0 0", fontSize: 13, color: "#999", fontWeight: 400 },
    primaryBtn: { padding: "10px 16px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 14 },
    chipBtn: { padding: "8px 12px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 },
    filtersCard: { display: "flex", gap: 14, alignItems: "flex-end", padding: 16, backgroundColor: "#fff", borderRadius: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 20, flexWrap: "wrap" },
    input: { width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#1f2937", outline: "none" },
    row: { display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" },
    statCard: { flex: "1 1 180px", display: "flex", alignItems: "center", gap: 12, padding: "18px 20px", backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", minWidth: 180 },
    statIcon: { width: 42, height: 42, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 },
    statLabel: { margin: 0, fontSize: 12, color: "#888", fontWeight: 500 },
    statValue: { margin: "2px 0 0", fontSize: 22, fontWeight: 700, lineHeight: 1.2 },
    card: { flex: "1 1 400px", padding: 24, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", minWidth: 320 },
    cardTitle: { fontSize: 15, fontWeight: 600, color: "#333", margin: "0 0 4px" },
    cardDesc: { margin: "0 0 14px", fontSize: 12, color: "#aaa", fontWeight: 400, lineHeight: 1.4 },
    empty: { color: "#ccc", textAlign: "center", padding: 40, fontSize: 14 },
    emptyCell: { color: "#ccc", textAlign: "center", padding: 20, fontSize: 13 },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", textAlign: "left", padding: "8px 6px", borderBottom: "1px solid #e5e7eb" },
    tr: { borderBottom: "1px solid #f5f5f5" },
    td: { padding: "8px 6px", fontSize: 13, color: "#374151" },
};
